import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import NoDataCard from '@/components/NoDataCard';
import Button from '@/components/UI/Button';
import Header from '@/components/UI/Header';
import Link from '@/components/UI/Link';
import SpreadSheet from '@/components/UI/SpreadSheet';
import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import SubHeader from '@/components/UI/SubHeader';
import { clearSessionStorage, errorToast } from '@/helpers';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { MetadataField } from '@/model/Metadata.model';
import type { Sample, SampleMeasurement } from '@/model/Sample.model';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import InVivoError from '@/model/InVivoError.ts';
import { ApiService } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { useFetchEntity } from '@/support/Hooks/fetch';
import useMountedState from '@/support/Hooks/fetch/useMountedState'; // Facilitates HoT's inability to utilise arrays
import { returnInitialMetaValues } from '@/support/hot';
import { web as webRoute } from '@/support/route';
import type HotTable from '@handsontable//react';
import type { CellProperties } from 'handsontable/settings';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getBaseSettings,
  ColumnWrapper,
  generateColumnData,
  generateSampleBulkUpdateList,
} from './BulkSampleEdit.utils'; // Facilitates HoT's inability to utilise arrays
import { modalAction } from '@/utils/modal';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';

// Facilitates HoT's inability to utilise arrays
export interface CustomSample extends Omit<Sample, 'details'> {
  details: Record<string, SampleMeasurement>;
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  reasonForChange: features?.reason_for_change ?? false,
  plannedSamples: features?.planned_samples ?? false,
}));

const BulkSampleEdit = () => {
  const navigate = useNavigate();
  const ref = useRef<HotTable>();
  const [data, setData] = useState<Array<CustomSample>>([]);
  const [apiError, setApiError] = useState<Error | false>(false);
  const [metadata, setMetadata] = useState<Array<MetadataField>>([]);
  const [sampleTypeLookup, setSampleTypeLookup] = useState<Record<string, TeamGlossaryV1>>();
  const [loading, setLoading] = useState(true);
  const [isInvalid, setIsInvalid] = useState<boolean>();
  const [columnData, updateColumnData] = useState<ColumnWrapper>({
    colHeaders: [],
    columns: [],
  });
  const dispatch = useDispatch();
  const { openModal, closeModal } = modalAction(dispatch);
  const isMounted = useMountedState();

  const { reasonForChange, plannedSamples } = useSelector(featureSelector);

  const { id: studyId } = useParams();
  const { selectedSamples } = useSelector(
    ({ samples: selectedSamples }: { samples: { selectedSamples: Array<Sample> } }) => selectedSamples
  );

  const { entity: sampleMetadata, entityLoading: sampleMetadataLoading } = useFetchEntity<Array<MetadataField>>({
    entityType: 'glossaryMeta',
    queryParams: {
      filter_type: 'sample_meta',
    },
  });

  const { loading: sampleTypesLoading, response: sampleTypesResponse } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    query: { group: 'samples', perPage: -1 },
    options: { onError: { toast: false, slug: 'bulk-sample-edit-sample-types-load' } },
  });

  const sampleTypeOptions =
    sampleTypesResponse?.type === 'success' ? (sampleTypesResponse.body?.data ?? []) : undefined;

  useEffect(() => {
    // Sample types are required for the details column in HoT as a lookup
    if (!sampleTypesLoading) {
      setSampleTypeLookup(
        sampleTypeOptions?.reduce<Record<string, TeamGlossaryV1>>((acc, type) => {
          if (type.title) {
            acc[type.title] = type;
          }
          return acc;
        }, {})
      );
    }
  }, [sampleTypesResponse]);

  const studyApiId = useMemo<string>(() => sessionStorage.getItem(`selectedStudyApiId${studyId}`) ?? '', [studyId]);

  const samples = useMemo<Array<CustomSample>>(() => {
    // The samples are forwarded from the API Table, and are saved in session in the event of
    // refreshing the page
    const sessionSamples = JSON.parse(
      sessionStorage.getItem(`selectedStudy${studyId}Samples`) ?? '[]'
    ) as Array<Sample>;
    return (_isEmpty(sessionSamples) ? selectedSamples : sessionSamples).map((sample) => ({
      ...sample,
      details: sample.details.reduce<Record<string, SampleMeasurement>>((acc, detail) => {
        if (_notNil(detail.key)) {
          acc[detail.key] = detail;
        }
        return acc;
      }, {}),
      meta: returnInitialMetaValues(sample.metadata),
    }));
  }, [studyId]);

  useEffect(() => {
    // Remove the API Table samples in the event the page navigates
    return () => {
      clearSessionStorage(`selectedStudy${studyId}Samples`, `/studies/${studyId}/samples/edit`)();
      clearSessionStorage(`selectedStudyApiId${studyId}`, `/studies/${studyId}/samples/edit`)();
    };
  }, [studyId]);

  useEffect(() => {
    const metadataMap = new Map();

    // Renders the available metadata to add to the table down to only the columns
    if (_isEmpty(metadata) && _notNil(sampleMetadata) && _notNil(samples) && _notNil(sampleTypeLookup)) {
      // Pre-compute Set of metadata IDs that exist on samples
      const existingMetadataIds = samples.reduce<Set<string>>((acc, sample) => {
        sample.metadata?.forEach((sampleMeta) => {
          const metaId = sampleMeta?.glossary_id ?? sampleMeta?.id;
          if (_notNil(metaId)) {
            acc.add(String(metaId));
          }
        });
        return acc;
      }, new Set());

      // Collect metadata fields that either have values on samples OR are required
      sampleMetadata.reduce((acc, meta) => {
        // Short-circuit: if required, skip the Set lookup entirely
        if (meta.required || existingMetadataIds.has(String(meta.id))) {
          acc.set(meta.id, {
            id: meta.id,
            glossary_id: meta.id,
            field_type: meta.field_type,
            options: meta.options,
            read_only: meta.read_only,
            title: meta.title,
            created_at: meta.created_at,
            updated_at: meta.updated_at,
            slug: meta.slug,
            active: true,
            required: meta.required,
          });
        }

        return acc;
      }, metadataMap);

      setMetadata(sampleMetadata.filter(({ id }) => !metadataMap.has(id)));

      updateColumnData(
        generateColumnData([...(metadataMap?.values() ?? [])], sampleTypeLookup?.[data[0].type], plannedSamples)
      );
    }
  }, [sampleMetadata, sampleTypeLookup, plannedSamples]);

  const settings = useMemo<CustomGridSettings>(
    () => ({
      ...getBaseSettings(plannedSamples),
      ...columnData,
      afterRenderer: function (
        TD: HTMLElement,
        _row: number,
        _col: number,
        _prop: string | number,
        _value: string,
        cellProperties: CellProperties & { linkedTooltipMessage?: string }
      ) {
        if (_notNil(cellProperties.linkedTooltipMessage)) {
          TD.setAttribute('data-tooltip-id', 'global-tooltip-id');
          TD.setAttribute('data-tooltip-content', cellProperties.linkedTooltipMessage);
        }
      },
    }),
    [columnData, plannedSamples]
  );

  useEffect(() => {
    const hot = ref.current?.hotInstance;
    if (_isNil(hot) || _isEmpty(data) || _isEmpty(settings.columns)) {
      return;
    }

    const columns = settings.columns;
    const unitColumnIndices = columns.reduce<Array<number>>((acc, column, col) => {
      if (column.isUnitColumn === true) {
        acc.push(col);
      }
      return acc;
    }, []);

    if (_isEmpty(unitColumnIndices)) {
      return;
    }

    data.forEach((sample, row) => {
      const isLinked = _notNil(sample.sample_definition_api_id);
      unitColumnIndices.forEach((col) => {
        hot.setCellMeta(row, col, 'readOnly', isLinked);
        hot.setCellMeta(row, col, 'className', isLinked ? 'htDimmed' : '');
        hot.setCellMeta(
          row,
          col,
          'linkedTooltipMessage',
          isLinked ? 'Units cannot be changed for samples linked to a sample plan' : undefined
        );
      });
    });

    hot.render();
  }, [data, settings.columns]);

  useEffect(() => {
    // Preserves the initial data for later comparison when building the PATCH body
    setData(structuredClone(samples));
    setLoading(false);
  }, [samples]);

  const saveSamples = async (reasonForChange?: string) => {
    setLoading(true);
    const sampleUpdateList = generateSampleBulkUpdateList(samples, data, settings.columns);
    try {
      await ApiService.call({
        endpoint: 'PATCH /api/v1/studies/{studyId}/samples/bulk',
        path: { studyId: studyApiId },
        body: { samples: sampleUpdateList, reason_for_change: reasonForChange },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-sample-edit-save' } },
      });
      navigate(webRoute('studies.samples', { id: studyId }));
      toast.success(`Successfully added ${samples.length > 1 ? 'samples' : 'sample'}`);
    } catch (error) {
      errorToast('Failed to save samples');
      ExceptionHandler.captureException(
        new InVivoError('Could not save samples', { cause: error, slug: 'bulk-sample-edit-save' })
      );
      if (isMounted()) {
        setApiError(error as Error);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  const saveAction = async () => {
    if (reasonForChange) {
      openModal('BULK_REASON_FOR_CHANGE', {
        title: 'Sample information has changed',
        onClick: saveSamples,
        closeModal,
      });
    } else {
      await saveSamples();
    }
  };

  const spreadSheet = (
    <>
      <div className="ow-spreadsheet-styles">
        <SpreadSheet
          data={data}
          settings={settings}
          className="pl4 pr4"
          innerRef={ref}
          metadata={metadata}
          setMetadata={setMetadata}
          setInvalid={setIsInvalid}
          maxRows={data.length}
          greedyValidateRow
        />
      </div>
      <div className="pa4">
        <Button
          onClick={saveAction}
          disabled={loading || isInvalid}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          Save
        </Button>
        <Button plain className="ml3" onClick={() => navigate(webRoute('studies.samples', { id: studyId }))}>
          Cancel
        </Button>
      </div>
    </>
  );

  return (
    <div className="h-100">
      {apiError && (
        <ApiErrorBanner
          className="mb4"
          title={'There was an error with your request'}
          text={
            'An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support.'
          }
          error={apiError}
        />
      )}
      {loading || sampleMetadataLoading || sampleTypesLoading || _isEmpty(settings) ? (
        <div style={{ height: 650 }}>
          <Loading txt={`${loading || sampleMetadataLoading ? 'Updating sample...' : 'Loading...'}`} />
        </div>
      ) : (
        <>
          <SubHeader linkToText="Samples" link={webRoute('studies.samples', { id: studyId })} />
          <div className="ph4">
            <Header mainHeaderText="Edit Samples" />
          </div>
          {_isNotEmpty(settings) && _isNotEmpty(data) ? (
            spreadSheet
          ) : (
            <div className="ma4">
              <NoDataCard
                title="You have not selected any samples"
                text={'Please select samples from your study to proceed'}
                NoDataComponent={
                  <Link to={webRoute('studies.samples', { id: studyId })} className="link blue">
                    Go to Samples
                  </Link>
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BulkSampleEdit;
